import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { useInView } from '../../hooks/useInView';

interface TestimonialItem {
  quote: string;
  name: string;
  event: string;
}

interface SectionTestimonialsProps {
  heading?: string;
  items?: TestimonialItem[];
}

const DEFAULT_TESTIMONIALS: TestimonialItem[] = [
  {
    quote:
      'Walking into Red Door Ranch, we both said "WOW" at the exact same moment. The space is absolutely breathtaking — from the grand staircase to the covered patio views. Tracy and Vernon made us feel like family from the very first tour. Our wedding was everything we dreamed of and more.',
    name: 'Ashley & Daniel',
    event: 'Wedding Reception',
  },
  {
    quote:
      'We toured several venues before visiting Red Door Ranch. The moment we stepped on the hilltop and saw the views, we knew immediately. The team was incredibly attentive throughout the entire planning process. Having exclusive access to the whole property meant no stress, no rush — just pure joy.',
    name: 'Maria & James',
    event: 'Wedding & Ceremony',
  },
  {
    quote:
      'Our corporate retreat at Red Door Ranch was unlike any event our company has hosted. The setting elevated the entire experience — people were genuinely inspired and connected. Tracy was so accommodating with every detail. We are already planning our next event here.',
    name: 'Jennifer L.',
    event: 'Corporate Retreat',
  },
];

interface CardProps {
  item: TestimonialItem;
  index: number;
  parentVisible: boolean;
}

function TestimonialCard({ item, index, parentVisible }: CardProps) {
  return (
    <div
      className={`fade-up bg-white/5 border border-white/10 p-10 lg:p-12 flex flex-col hover:border-rdr-gold/30 transition-colors duration-500 ${parentVisible ? 'is-visible' : ''}`}
      style={{ transitionDelay: `${index * 0.18}s` }}
    >
      <div className="text-5xl font-serif text-rdr-gold/25 leading-none mb-6 select-none">&ldquo;</div>
      <p className="font-serif text-white/88 text-lg lg:text-xl leading-relaxed italic flex-1 mb-10">
        {item.quote}
      </p>
      <div className="flex items-center justify-between pt-6 border-t border-white/10">
        <div>
          <div className="flex items-center gap-1 mb-2">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="w-3 h-3 flex items-center justify-center">
                <i className="ri-star-fill text-xs text-rdr-gold" />
              </div>
            ))}
          </div>
          <p className="font-serif text-white text-base">{item.name}</p>
          <p className="text-white/40 font-sans text-xs tracking-widest uppercase mt-0.5">{item.event}</p>
        </div>
        <div className="w-10 h-10 flex items-center justify-center border border-rdr-gold/20 flex-shrink-0">
          <i className="ri-double-quotes-r text-lg text-rdr-gold/50" />
        </div>
      </div>
    </div>
  );
}

export default function SectionTestimonials({
  heading = 'From Our Couples & Clients',
  items = DEFAULT_TESTIMONIALS,
}: SectionTestimonialsProps) {
  const sectionRef = useRef<HTMLElement>(null);
  const { ref: headRef, isVisible: headVisible } = useInView();
  const { ref: gridRef, isVisible: gridVisible } = useInView({ threshold: 0.05 });
  const { ref: ctaRef, isVisible: ctaVisible } = useInView();

  return (
    <section
      ref={sectionRef}
      className="relative bg-rdr-dark py-24 lg:py-32 overflow-hidden"
    >
      {/* Background image with deep overlay */}
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/a4d2e0cda599ac6f529eebb6e14e41e4.jpg"
          alt=""
          aria-hidden="true"
          className="w-full h-full object-cover object-center opacity-10"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-rdr-dark via-rdr-dark/95 to-rdr-dark" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12">
        {/* Header */}
        <div
          ref={headRef as React.RefObject<HTMLDivElement>}
          className={`text-center mb-16 fade-up ${headVisible ? 'is-visible' : ''}`}
        >
          <div className="inline-flex items-center gap-4 mb-5">
            <span className="gold-divider" />
            <p className="text-rdr-gold text-xs tracking-[0.25em] uppercase font-sans">{heading}</p>
            <span className="gold-divider" />
          </div>
          <h2 className="font-serif text-white text-4xl lg:text-5xl xl:text-6xl max-w-2xl mx-auto leading-tight">
            Celebrations That Speak for Themselves
          </h2>
        </div>

        {/* Testimonials grid */}
        <div
          ref={gridRef as React.RefObject<HTMLDivElement>}
          className="grid grid-cols-1 lg:grid-cols-3 gap-5"
        >
          {items.map((item, i) => (
            <TestimonialCard key={i} item={item} index={i} parentVisible={gridVisible} />
          ))}
        </div>

        {/* Post-testimonials CTA */}
        <div
          ref={ctaRef as React.RefObject<HTMLDivElement>}
          className={`mt-14 flex flex-col sm:flex-row items-center justify-center gap-5 fade-up ${ctaVisible ? 'is-visible' : ''}`}
        >
          <Link
            to="/location#contact"
            className="inline-flex items-center gap-3 bg-rdr-gold text-white font-sans text-xs tracking-[0.22em] uppercase px-10 py-4 hover:bg-rdr-gold-light transition-colors duration-300 whitespace-nowrap cursor-pointer"
          >
            Book Your Tour Today
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-right-line text-sm" />
            </div>
          </Link>
          <a
            href="https://www.google.com/maps/place/Red+Door+Ranch"
            target="_blank"
            rel="nofollow noreferrer"
            className="inline-flex items-center gap-3 border border-white/20 text-white/70 font-sans text-xs tracking-widest uppercase px-8 py-4 hover:border-rdr-gold hover:text-rdr-gold transition-colors duration-300 cursor-pointer whitespace-nowrap"
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-google-line text-sm" />
            </div>
            Read Our Google Reviews
          </a>
        </div>
      </div>
    </section>
  );
}
